import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { QRCodeSVG } from 'qrcode.react';
import ShippingLabelContent from './components/ShippingLabel/ShippingLabelContent'; 
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebase';

const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // A page that links here can leave a breadcrumb in router state saying where
  // to return to, so the back button restores that exact view (the Driver
  // Analytics tab with its driver and filters) instead of a bare history step,
  // which would lose it. Without one, fall back to the previous page.
  const backTo = location.state?.from || null;
  const backLabel = location.state?.fromLabel
    ? `Back to ${location.state.fromLabel}`
    : 'Back to List';
  const goBack = () => (backTo ? navigate(backTo) : navigate(-1));
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState({});
  const [isAdmin, setIsAdmin] = useState(false); // Set this based on your auth logic
  const [isOwner, setIsOwner] = useState(false);
  const [user] = useAuthState(auth); // Get logged-in user
  
  const canEdit = isAdmin || isOwner

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [showSaveWarning, setShowSaveWarning] = useState(false);
  const [editedPkg, setEditedPkg] = useState(null);
  
  // QR code ref
  const qrCodeRef = useRef(null);
  const printRef = useRef();
  const [showLabel, setShowLabel] = useState(false);

  // Company options for dropdown
  const companyOptions = ['Limitless Fashions', 'Jenz', 'Denim World'];

  // Status options for dropdown
  const baseStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'left-store', label: 'Left Store' },
    { value: 'in-store', label: 'In Store' },
    { value: 'returning-to-store', label: 'Returning to Store' }
  ];

  // Only admins see "Returned" and "Paid" in the dropdown
  const statusOptions = isAdmin
    ? [
        ...baseStatusOptions,
        { value: 'returned', label: 'Returned' },
        { value: 'paid', label: 'Paid' }
      ]
    : baseStatusOptions;

  useEffect(() => {
    const fetchPackage = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'packages', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const packageData = { id: docSnap.id, ...docSnap.data() };
          setPkg(packageData);
          setEditedPkg(packageData); // Initialize edit form with current data
        } else {
          setError('Package not found');
        }
      } catch (err) {
        console.error('Error fetching package:', err);
        setError('Failed to load package details');
      } finally {
        setLoading(false);
      }
    };

    fetchPackage();
  }, [id]);

  // Fetch all users for dropdown (admin only)
  useEffect(() => {
    const fetchUsers = async () => {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = {};
      usersSnapshot.forEach(doc => {
        usersData[doc.id] = { id: doc.id, ...doc.data() };
      });
      setUsers(usersData);
    };
    fetchUsers();
  }, []);

  // Example: Set isAdmin based on your auth/user logic
  useEffect(() => {
    if (user && users[user.uid]) {
      setIsAdmin(Array.isArray(users[user.uid].roles) && users[user.uid].roles.includes('admin'));
    } else {
      setIsAdmin(false);
    }
  }, [user, users]);

    useEffect(() => {
    if (user && users[user.uid]) {
      setIsOwner(Array.isArray(users[user.uid].roles) && users[user.uid].roles.includes('owner'));
    } else {
      setIsOwner(false);
    }
  }, [user, users]);

  // Download QR code as an image
  const downloadQRCode = () => {
    if (!qrCodeRef.current || !pkg?.trackingNumber) return;
    
    const svg = qrCodeRef.current.querySelector('svg');
    if (!svg) return;
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Create an image from the SVG
    const image = new Image();
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    image.onload = () => {
      // Set canvas dimensions
      canvas.width = image.width;
      canvas.height = image.height;
      
      // Draw the image to the canvas
      ctx.drawImage(image, 0, 0);
      
      // Convert canvas to data URL
      const dataURL = canvas.toDataURL('image/png');
      
      // Create download link
      const link = document.createElement('a');
      link.href = dataURL;
      link.download = `qr-${pkg.trackingNumber}.png`;
      
      // Append to document, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Revoke the object URL to free memory
      URL.revokeObjectURL(svgUrl);
    };
    
    image.src = svgUrl;
  };

  // Handle input changes in edit mode
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedPkg(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle save button click
  const handleSaveClick = () => {
    setShowSaveWarning(true);
  };

  // Handle actual save operation
  const handleConfirmSave = async () => {
    try {
      const docRef = doc(db, 'packages', id);
      
      // Add update timestamp and history entry
      const now = new Date();
      const historyEntry = {
        status: editedPkg.status,
        timestamp: now,
        note: `Status updated to ${editedPkg.status}`
      };
      
      // Prepare update data
      const updateData = {
        ...editedPkg,
        updatedAt: now,
        history: [...(editedPkg.history || []), historyEntry]
      };
        
      if (!canEdit) {
      updateData.total = pkg.total;
      }
      // Update Firestore
      await updateDoc(docRef, updateData);
      
      // Update local state
      setPkg(updateData);
      setShowSaveWarning(false);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating package:', err);
      alert('Failed to update package. Please try again.');
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditedPkg(pkg); // Reset to original data
    setIsEditing(false);
  };

  const formatDate = (seconds) => {
    if (!seconds) return 'N/A';
    
    const date = new Date(seconds * 1000);
    const options = { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    return date.toLocaleDateString(undefined, options);
  };

  const getStatusInfo = (status) => {
    const statusTypes = {
      'delivered': { 
        color: 'bg-green-100 text-green-800',
        icon: (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        )
      },
      'in-transit': { 
        color: 'bg-blue-100 text-blue-800',
        icon: (
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
          </svg>
        )
      },
      'canceled': { 
        color: 'bg-red-100 text-red-800',
        icon: (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m6 4.125 2.25 2.25m0 0 2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"></path>
          </svg>
        )
      },
      'pending': { 
        color: 'bg-gray-100 text-gray-800',
        icon: (
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      'returned': { 
        color: 'bg-red-100 text-red-800',
        icon: (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        )
      },
      'in-store': { 
      color: 'bg-gray-100 text-gray-800',
      icon: (
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75"></path>
        </svg>
      )
      },
      'left-store': { 
      color: 'bg-blue-100 text-blue-800',
      icon: (
        <svg className="w-4 h-4 text-red-500" fill="blue" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px">
          <path d="M200-120q-50 0-85-35t-35-85q0-50 35-85t85-35h560q50 0 85 35t35 85q0 50-35 85t-85 35H200Zm0-80h560q17 0 28.5-11.5T800-240q0-17-11.5-28.5T760-280H200q-17 0-28.5 11.5T160-240q0 17 11.5 28.5T200-200Zm200-240q-17 0-28.5-11.5T360-480v-320q0-17 11.5-28.5T400-840h320q17 0 28.5 11.5T760-800v320q0 17-11.5 28.5T720-440H400Zm40-80h240v-240H440v240Zm-360-2v-77h197v77H80Zm400-118h160v-79H480v79Zm-320 0h117v-79H160v79Zm280 120v-240 240Z"/>
        </svg>
      )
      },
      'paid': { 
      color: 'bg-purple-100 text-purple-800',
      icon: (
        <svg className="w-4 h-4 text-purple-500" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
          <path d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
        </svg>
      )
      },
    };
    
    return statusTypes[status?.toLowerCase()] || statusTypes['pending'];
  };

  // Print shipping label
  const handlePrint = async () => {
    setShowLabel(true);
    setTimeout(() => {
      try {
        window.print();
        setShowLabel(false);
      } catch (e) {
        downloadPdf();
      }
    }, 500); // Try 300ms or higher if needed
  };

  // Download shipping label as PDF
  const downloadPdf = async () => {
    const element = printRef.current;
    if (!element) return;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true });
    const data = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: "a4" });
    const imgProperties = pdf.getImageProperties(data);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;
    pdf.addImage(data, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`shipping-label-${pkg.trackingNumber}.pdf`);
  };

  if (loading) return (
    <div className="p-6 max-w-4xl mx-auto flex justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  if (error) return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <p>{error}</p>
        <button
          onClick={goBack}
          className="mt-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
        >
          Go Back
        </button>
      </div>
    </div>
  );

  if (!pkg) return <p className="p-6">No package data found.</p>;

  const displayPackage = isEditing ? editedPkg : pkg;
  const statusInfo = getStatusInfo(displayPackage.status);

  // Helper to get user display name
  const getUserDisplay = (userId) => {
    const user = users[userId];
    if (!user) return userId || 'N/A';
    return user.name || user.email || user.displayName || userId;
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 p-6 border-b">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <div>
              {/* Only rendered when the linking page left a breadcrumb, so it
                  never claims a trail that does not exist. */}
              {backTo && (
                <nav aria-label="Breadcrumb" className="mb-1">
                  <ol className="flex items-center gap-1 text-sm text-gray-500">
                    <li>
                      <button
                        onClick={goBack}
                        className="hover:text-blue-600 hover:underline"
                      >
                        {location.state?.fromLabel || 'Back'}
                      </button>
                    </li>
                    <li aria-hidden="true">/</li>
                    <li className="text-gray-700 font-medium" aria-current="page">
                      Package Details
                    </li>
                  </ol>
                </nav>
              )}
              <div className="flex items-center">
                <h1 className="text-2xl font-bold text-gray-800">Package Details</h1>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`ml-3 p-1 rounded ${isEditing ? 'bg-gray-200' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}
                  title={isEditing ? "Currently editing" : "Edit package"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 0L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mt-1">Created on {formatDate(pkg.createdAt?.seconds)}</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center">
              {isEditing ? (
                <select
                  name="status"
                  value={editedPkg.status}
                  onChange={handleInputChange}
                  className="p-2 border border-gray-300 rounded text-sm font-medium"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                  {statusInfo.icon}
                  {displayPackage.status.charAt(0).toUpperCase() + displayPackage.status.slice(1)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Package Details with QR Code */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Left Column - Package Info */}
            <div className="md:col-span-3">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Number</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="trackingNumber"
                      value={editedPkg.trackingNumber}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded font-mono"
                      required
                    />
                  ) : (
                    <p className="font-mono font-medium">{displayPackage.trackingNumber}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sender</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="sender"
                      value={editedPkg.sender}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  ) : (
                    <p>{displayPackage.sender || 'N/A'}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  {isEditing ? (
                    <select
                      name="company"
                      value={editedPkg.company || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="">Select Company...</option>
                      {companyOptions.map(company => (
                        <option key={company} value={company}>
                          {company}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p>{displayPackage.company || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipient</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="recipient"
                      value={editedPkg.recipient}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  ) : (
                    <p>{displayPackage.recipient || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="details"
                      value={editedPkg.details}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  ) : (
                    <p>{displayPackage.details || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone#</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="details"
                      value={editedPkg.phone}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  ) : (
                    <p>{displayPackage.phone || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Destination</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="destination"
                      value={editedPkg.destination}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  ) : (
                    <p>{displayPackage.destination || 'N/A'}</p>
                  )}
                </div>
                
                {/* New Fields: Total and Assigned To */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total</label>
                  {isEditing && canEdit ? (
                    <input
                      type="number"
                      name="total"
                      value={editedPkg.total}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter total amount"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      pattern="^\d+(\.\d{1,2})?$"
                      onKeyDown={e => {
                        if (
                          ['e', 'E', '+', '-'].includes(e.key)
                          || (e.key === '.' && String(editedPkg.total).includes('.'))
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  ) : (
                    <p>TT$ {displayPackage.total || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Driver Assigned</label>
                  {isEditing && isAdmin ? (
                    <select
                      name="assignedTo"
                      value={editedPkg.assignedTo}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="">Select a driver</option>
                      {Object.values(users)
                        .filter(u => Array.isArray(u.roles) && u.roles.includes('driver'))
                        .map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name || user.email || user.displayName || user.id}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <p>{getUserDisplay(displayPackage.assignedTo)}</p>
                  )}
                </div>

                {/* Read-only: staff tag themselves on Add Package and the staff
                    report groups on this, so it shouldn't drift after the fact. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created By</label>
                  <p>{getUserDisplay(displayPackage.createdBy)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                  <p>{displayPackage.updatedAt?.seconds ? formatDate(displayPackage.updatedAt?.seconds) : 'N/A'}</p>
                </div>

                {/* Edit Buttons */}
                {isEditing && (
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveClick}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Column - QR Code */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-gray-700 text-center mb-4">Tracking QR Code</h3>
              <div 
                ref={qrCodeRef} 
                className="w-full h-48 flex items-center justify-center bg-gray-50 mb-4 rounded-md"
              >
                {pkg.trackingNumber ? (
                  <QRCodeSVG 
                    value={"https://pacepaktt.com/package/"+pkg.id}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                ) : (
                  <p className="text-gray-400">No tracking number available</p>
                )}
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-3 break-all font-mono">
                  {pkg.trackingNumber}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="mt-6 p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <button
            onClick={goBack}
            className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {backLabel}
          </button>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={downloadQRCode}
              className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download QR
            </button>
            
            <button
              onClick={() => {
                navigate(`/shipping-label/${pkg.id}?print=true`);
              }}
              className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            
            {!pkg.isPaid && (
              <button
                onClick={() => navigate(`/paymentForm/${pkg.id}`)}
                className="w-full sm:w-auto px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Record Payment
              </button>
            )}
          </div>
        </div>
        
        {/* Save Confirmation Warning */}
        {showSaveWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center text-yellow-500 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-lg font-bold">Confirm Changes</h3>
              </div>
              <p className="mb-6">
                Are you sure you want to save these changes? This will update the package information and add a new entry to the status history.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowSaveWarning(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSave}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Confirm Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hidden label for printing */}
        {showLabel && (
          <div
            className="print-overlay"
            style={{
              position: 'fixed',
              top: 0, left: 0, width: '100vw', height: '100vh',
              background: 'white', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <ShippingLabelContent pkg={pkg} formatDate={formatDate} printRef={printRef} />
          </div>
        )}
    </div>
  </div>
  );
};

export default PackageDetail;